/**
 * Tiny, self-contained parser for the blog posts authored as markdown in
 * `src/content/blog/`. Not a general Markdown implementation — it only
 * understands the small subset those posts use (see the comment at the top of
 * any `.md` file there): flat `key: value` frontmatter, a lede, `## ` feature
 * headings, `- ` lists, `> ` callouts, the `:chips:` and `:example:`
 * directives, a `---` outro separator, and the inline markers
 * `` `translit` ``, `` `ไทย` `` (a headword, linked to its entry),
 * `*emphasis*` and inline Thai text.
 */

export type Tone = 'sky' | 'coral' | 'marigold' | 'teal' | 'violet'

const TONES: readonly Tone[] = ['sky', 'coral', 'marigold', 'teal', 'violet']

const toTone = (value: string): Tone => (TONES.includes(value as Tone) ? (value as Tone) : 'sky')

export type Chip = { label: string; tone: Tone }

export type Meta = {
   title: string
   subtitle: string
   metaDescription: string
   chips: Chip[]
   author: string
   date: string
   readTime: string
   heroImage: string
   heroAlt: string
   ctaEyebrow: string
   ctaTitle: string
   ctaText: string
   ctaButton: string
   ctaDoneTitle: string
   ctaDoneText: string
   ctaError: string
}

// One entry in an `:example:` grid: a headword shown in a single sense,
// with the sentence that illustrates it.
export type Example = {
   word: string
   sense: string
   thai: string
   translit: string
   english: string
}

export type Block =
   | { type: 'paragraph'; text: string }
   | { type: 'list'; items: string[] }
   | { type: 'callout'; icon: string; text: string }
   | { type: 'chips'; chips: string[]; note: string }
   | { type: 'examples'; examples: Example[] }

export type Feature = { title: string; blocks: Block[] }

export type Post = {
   meta: Meta
   lede: Block[]
   features: Feature[]
   outro: Block[]
}

export type Token =
   | { kind: 'text'; value: string }
   | { kind: 'translit'; value: string }
   | { kind: 'em'; value: string }
   | { kind: 'thai'; value: string }
   | { kind: 'headword'; value: string }
   | { kind: 'email'; value: string }

// Split `text` on `re`, keeping the surrounding plain text as raw strings and
// turning each match into a Token via `make`.
const splitByRegex = (
   text: string,
   re: RegExp,
   make: (match: RegExpExecArray) => Token
): (string | Token)[] => {
   const out: (string | Token)[] = []
   let last = 0
   re.lastIndex = 0
   let match = re.exec(text)
   while (match !== null) {
      if (match.index > last) out.push(text.slice(last, match.index))
      out.push(make(match))
      last = match.index + match[0].length
      match = re.exec(text)
   }
   if (last < text.length) out.push(text.slice(last))
   return out
}

const THAI_RUN = /[\u0E00-\u0E7F]+/g

const EMAIL = /[^\s()<>@]+@[^\s()<>@]+\.[^\s()<>@]+/g

// A code span holding nothing but Thai script is a headword the post is
// pointing at \u2014 "Look at `\u0E1C\u0E21` for example" \u2014 rather than a romanisation, so it
// becomes a link to that word's dictionary entry. Thai in ordinary prose stays
// plain text: those are greetings (\u0E2A\u0E27\u0E31\u0E2A\u0E14\u0E35\u0E04\u0E23\u0E31\u0E1A), not words being cited.
const THAI_HEADWORD = /^[\u0E00-\u0E7F]+$/

const codeToken = (value: string): Token =>
   THAI_HEADWORD.test(value) ? { kind: 'headword', value } : { kind: 'translit', value }

// Tokenise one line of inline text: code spans first (their contents are left
// verbatim), then emphasis, then any remaining Thai runs.
export const tokenize = (text: string): Token[] => {
   const afterCode = splitByRegex(text, /`([^`]+)`/g, (m) => codeToken(m[1]))

   const afterEm: (string | Token)[] = []
   for (const part of afterCode) {
      if (typeof part !== 'string') {
         afterEm.push(part)
         continue
      }
      afterEm.push(...splitByRegex(part, /(\*|_)(.+?)\1/g, (m) => ({ kind: 'em', value: m[2] })))
   }

   const afterEmail: (string | Token)[] = []
   for (const part of afterEm) {
      if (typeof part !== 'string') {
         afterEmail.push(part)
         continue
      }
      afterEmail.push(...splitByRegex(part, EMAIL, (m) => ({ kind: 'email', value: m[0] })))
   }

   const tokens: Token[] = []
   for (const part of afterEmail) {
      if (typeof part !== 'string') {
         tokens.push(part)
         continue
      }
      for (const piece of splitByRegex(part, THAI_RUN, (m) => ({ kind: 'thai', value: m[0] }))) {
         tokens.push(typeof piece === 'string' ? { kind: 'text', value: piece } : piece)
      }
   }
   return tokens
}

const parseFrontmatter = (raw: string): Record<string, string> => {
   const fields: Record<string, string> = {}
   for (const line of raw.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith('#')) continue
      const colon = trimmed.indexOf(':')
      if (colon === -1) continue
      fields[trimmed.slice(0, colon).trim()] = trimmed.slice(colon + 1).trim()
   }
   return fields
}

const parseChips = (raw: string): Chip[] =>
   raw
      .split(';')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
         const [label, tone] = entry.split('|').map((part) => part.trim())
         return { label: label ?? '', tone: toTone(tone ?? '') }
      })

const buildMeta = (fields: Record<string, string>): Meta => ({
   title: fields.title ?? '',
   subtitle: fields.subtitle ?? '',
   metaDescription: fields.metaDescription ?? '',
   chips: parseChips(fields.chips ?? ''),
   author: fields.author ?? '',
   date: fields.date ?? '',
   readTime: fields.readTime ?? '',
   heroImage: fields.heroImage ?? '',
   heroAlt: fields.heroAlt ?? '',
   ctaEyebrow: fields.ctaEyebrow ?? '',
   ctaTitle: fields.ctaTitle ?? '',
   ctaText: fields.ctaText ?? '',
   ctaButton: fields.ctaButton ?? '',
   ctaDoneTitle: fields.ctaDoneTitle ?? '',
   ctaDoneText: fields.ctaDoneText ?? '',
   ctaError: fields.ctaError ?? '',
})

// What a single body line is asking for. Everything that isn't one of the
// prefixes below is prose.
type LineKind = 'bullet' | 'callout' | 'chips' | 'example' | 'text'

const CHIPS = ':chips:'
const EXAMPLE = ':example:'

const lineKind = (line: string): LineKind => {
   if (line.startsWith('- ')) return 'bullet'
   if (line.startsWith('> ')) return 'callout'
   if (line.startsWith(CHIPS)) return 'chips'
   if (line.startsWith(EXAMPLE)) return 'example'
   return 'text'
}

// `> text` — a highlighted aside. An optional `:icon:` prefix names the
// Material icon to show.
const CALLOUT_ICON = /^:([a-z_]+):\s*/

// The heart is the donate thank-yous' own mark, so it is never the default — a
// callout that wants it asks with `:favorite:`. An unmarked aside gets no glyph
// at all: the wash already sets it apart, and the lines pulled out this way
// carry their own quotation marks, which a quote icon only doubles up on.
const toCallout = (lines: string[]): Block => {
   const text = lines.map((line) => line.slice(2).trim()).join(' ')
   const icon = text.match(CALLOUT_ICON)
   return {
      type: 'callout',
      icon: icon ? icon[1] : '',
      text: icon ? text.slice(icon[0].length) : text,
   }
}

// `:chips: a | b | c -> trailing note` — a row of romanisation chips, with an
// optional muted note after the arrow.
const toChips = (line: string): Block => {
   const [labels, note] = line.slice(CHIPS.length).split('->')
   return {
      type: 'chips',
      chips: (labels ?? '')
         .split('|')
         .map((chip) => chip.trim())
         .filter(Boolean),
      note: note?.trim() ?? '',
   }
}

// `:example: word | sense | thai | translit | english` — one bubble per line;
// consecutive lines make up a single grid.
const toExamples = (lines: string[]): Block => ({
   type: 'examples',
   examples: lines.map((line) => {
      const [word, sense, thai, translit, english] = line
         .slice(EXAMPLE.length)
         .split('|')
         .map((field) => field.trim())
      return {
         word: word ?? '',
         sense: sense ?? '',
         thai: thai ?? '',
         translit: translit ?? '',
         english: english ?? '',
      }
   }),
})

// Turn a run of consecutive non-blank lines into blocks, one per run of lines
// of the same kind — so a paragraph followed by three `:example:` lines is a
// paragraph block and one examples block.
const toBlocks = (lines: string[]): Block[] => {
   const blocks: Block[] = []
   let group: string[] = []
   let kind: LineKind = 'text'

   const flushGroup = () => {
      if (group.length === 0) return
      switch (kind) {
         case 'bullet':
            blocks.push({ type: 'list', items: group.map((line) => line.slice(2).trim()) })
            break
         case 'callout':
            blocks.push(toCallout(group))
            break
         case 'chips':
            for (const line of group) blocks.push(toChips(line))
            break
         case 'example':
            blocks.push(toExamples(group))
            break
         case 'text':
            blocks.push({ type: 'paragraph', text: group.join(' ').trim() })
            break
      }
      group = []
   }

   for (const line of lines) {
      const next = lineKind(line)
      if (next !== kind) {
         flushGroup()
         kind = next
      }
      group.push(line)
   }
   flushGroup()

   return blocks
}

const parseBody = (body: string): Pick<Post, 'lede' | 'features' | 'outro'> => {
   const lede: Block[] = []
   const features: Feature[] = []
   const outro: Block[] = []

   let section: 'lede' | 'features' | 'outro' = 'lede'
   let current: Feature | null = null
   let buffer: string[] = []

   const target = (): Block[] => (section === 'outro' ? outro : current ? current.blocks : lede)

   const flush = () => {
      if (buffer.length > 0) {
         target().push(...toBlocks(buffer))
         buffer = []
      }
   }

   for (const rawLine of body.split('\n')) {
      const line = rawLine.trimEnd()
      if (line.trim() === '') {
         flush()
      } else if (line.trim() === '---') {
         flush()
         section = 'outro'
         current = null
      } else if (line.startsWith('## ')) {
         flush()
         current = { title: line.slice(3).trim(), blocks: [] }
         features.push(current)
         section = 'features'
      } else {
         buffer.push(line.trim())
      }
   }
   flush()

   return { lede, features, outro }
}

export const parsePost = (raw: string): Post => {
   const normalised = raw.replace(/\r\n/g, '\n')
   const match = normalised.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/)
   const frontmatter = match ? match[1] : ''
   const body = match ? match[2] : normalised

   return {
      meta: buildMeta(parseFrontmatter(frontmatter)),
      ...parseBody(body),
   }
}
